import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { CalendarDays, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/events-report/")({
  head: () => ({ meta: [{ title: "Etkinlik Raporları — Admin" }] }),
  component: EventsReportIndex,
});

interface EventRow {
  id: string;
  name: string;
  starts_at: string;
  venue: string | null;
  cover_image_url: string | null;
  status: string;
}

async function fetchAll<T>(build: () => any, pageSize = 1000): Promise<T[]> {
  const all: T[] = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await build().range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data as T[]) ?? [];
    all.push(...rows);
    if (rows.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

function EventsReportIndex() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [checkinsByEvent, setCheckinsByEvent] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) void navigate({ to: "/" });
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    (async () => {
      setBusy(true);
      const { data: evs } = await supabase
        .from("events")
        .select("id, name, starts_at, venue, cover_image_url, status")
        .order("starts_at", { ascending: false });
      const rows = (evs as EventRow[]) ?? [];
      setEvents(rows);
      const tickets = await fetchAll<{ event_id: string; checked_in_at: string | null }>(() =>
        supabase.from("tickets").select("event_id, checked_in_at"),
      );
      const byEv: Record<string, number> = {};
      for (const t of tickets) {
        if (t.checked_in_at) byEv[t.event_id] = (byEv[t.event_id] ?? 0) + 1;
      }
      setCheckinsByEvent(byEv);
      setBusy(false);
    })();
  }, [user, isAdmin]);

  const past = useMemo(
    () => events.filter((e) => new Date(e.starts_at).getTime() < Date.now()),
    [events],
  );

  if (loading || !user || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-6xl px-4 py-10 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">Admin</h1>
        <AdminTabs />
        <h2 className="mb-4 text-xl font-semibold">Geçmiş etkinlikler</h2>
        {busy ? (
          <div className="text-muted-foreground">Yükleniyor...</div>
        ) : past.length === 0 ? (
          <div className="text-muted-foreground">Henüz geçmiş etkinlik yok.</div>
        ) : (
          <div className="grid gap-3">
            {past.map((e) => (
              <Link
                key={e.id}
                to="/harikaradmin/events-report/$eventId"
                params={{ eventId: e.id }}
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-card transition-colors hover:bg-muted/40"
              >
                {e.cover_image_url ? (
                  <img
                    src={e.cover_image_url}
                    alt=""
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-muted">
                    <CalendarDays className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{e.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(e.starts_at).toLocaleString("tr-TR", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    {e.venue ? ` — ${e.venue}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{checkinsByEvent[e.id] ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Giriş</div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
